/* eslint-disable no-underscore-dangle */
import { useNavigation } from '@react-navigation/native';
import {
    useEffect,
    useState
} from 'react';
import { useSelector } from 'react-redux';

import {
    InfoList,
    Link,
    Page,
    React,
    Section,
    Subtitle,
    Title
} from '../../common';
import { ClickableTitle } from '../../common/elements';
import { View } from '../../common/react';
import { useDoctorDescriptions } from '../../hooks/doctorDescription';
import { getClinicsWithBumbaByType } from '../../selectors';
import { COLORS } from '../../styles/colors';
import { infoList as styles } from '../../styles/common';
import { sortBy } from '../../utils';
import { deepCloneObject } from '../../utils/objects';
import { useController } from './DoctorDetails';
import { sortClinics } from './utils';

/**
 * DoctorInfo Page (☆☆☆☆☆).
 */

export const DoctorInfo = Page.register(({ doctorInfo }) => {
    const bumbaClinics = useSelector(getClinicsWithBumbaByType);
    const navigation = useNavigation();

    const [ docInfo,
        setDocInfo ] = useState();
    // const clinics = useSelector(getMedcentersListWithPrograms);

    const hasBumbaTest = (clinicId) => !!(bumbaClinics ?? {})[clinicId]?.bumbaTestsSupport;
    const hasBumbaReport = (clinicId) => !!(bumbaClinics ?? {})[clinicId]?.bumbaReportsSupport;
    const hasBumba = (clinicId) => hasBumbaTest(clinicId) || hasBumbaReport(clinicId);

    doctorInfo.worksAt = sortClinics(
        doctorInfo?.worksAt?.map((clinic) => {
            clinic.hasBumba = hasBumba(clinic.id);
            return clinic;
        }),
    );

    const DoctorInfoClinic = ({ info }) => {
        if (Array.isArray(info)) {
            return (
                <View
                    style={{
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                    }}
                >
                    {info.map((i, index) => (
                        <DoctorInfoClinic
                            key={index}
                            info={i}
                        />
                    ))}
                </View>
            );
        }
    };

    const doc = useController(doctorInfo?._id ?? doctorInfo?.id);

    const {
        descriptions,
        doctorDetails,
    } = useDoctorDescriptions(
        doctorInfo?._id ?? doctorInfo?.id,
        doc?.doctorInfo ?? doctorInfo,
    );
    const mergeDescriptions = (d, info) => {
        const newD = deepCloneObject(d);
        if (!info) {
            return d;
        }
        for (let j = 0; j < d.worksAt?.length; j++) {
            const clinic = deepCloneObject(d.worksAt?.[j]);
            const i = info?.find?.((c) => c?.branchId === clinic?.id);
            clinic.description = i?.description;
            clinic.head = i?.providerCode;
            newD.worksAt[j] = clinic;
        }

        if (typeof newD?.profile === 'object' && newD?.profile !== null && !Array.isArray(newD?.profile)) {
            newD.profile = Object.values(newD.profile);
        }

        return newD;
    };

    useEffect(() => {
        const merged = mergeDescriptions(doctorInfo, descriptions);
        setDocInfo(merged);
    }, [ doctorInfo,
        descriptions ]);
    // }, [doctorInfo, doc.doctorInfo, descriptions]);

    return (
        <Page name="doctor-info">
            <Section title="titles.general_info">
                <InfoList
                    listFooterText={doctorInfo.overview}
                    fields="doctorsInfoFieldsWithDescription"
                    data={{
                        ...doctorDetails,
                        ...docInfo,
                    }}
                    customRenderer={(item, field, title) => {
                        const id = item?.id;
                        const d = item?.data?.[id];
                        const out = [];
                        if (!d && !Array.isArray(d)) {
                            return null;
                        }
                        const data = [
                            ...d.map?.((i) => ({
                                ...i,
                                name: i?.name,
                                address: i?.address,
                                branchId: i?.clinicId,
                                use: false,
                            })),
                        ];
                        const length = data?.length;
                        let order = 0;
                        for (let i = 0; i < length; i += 1) {
                            if (!data[i].use) {
                                data[i].use = true;
                                const _id = data?.[i]?.[field] ?? '';
                                const _descr = data?.[i]?.description;
                                if (_id) {
                                    out.push({
                                        order,
                                        id: _id,
                                        [field]: data[i],
                                        data: [ data[i] ],
                                    });
                                    order += 1;
                                    for (let j = 0; j < length; j += 1) {
                                        if (
                                            !data[j].use &&
                                            (data[j][field] ?? '') === _id &&
                                            data[j].description === _descr
                                        ) {
                                            data[j].use = true;
                                            out[order - 1].data.push(data[j]);
                                        }
                                    }
                                }
                            }
                        }

                        const paddingSize = 12;
                        const doneDescriptions = [];

                        const Description = ({ description }) => {
                            if (!description) {
                                return null;
                            }
                            if (doneDescriptions.includes(description?.trim?.())) {
                                return null;
                            }
                            doneDescriptions.push(description?.trim?.());
                            return (
                                <View
                                    style={[
                                        {
                                            flexDirection: 'row',
                                            paddingBottom: paddingSize,
                                            paddingTop: paddingSize,
                                            borderBottomWidth: 1,
                                            borderBottomColor: COLORS.BORDER_GRAY,
                                            paddingHorizontal: 6,
                                        },
                                    ]}
                                >
                                    <Title style={[ styles.title ]}>{`${Object.R('titles.doctorDescription')}:`}</Title>
                                    <View style={[ styles.rightTitleBlock,
                                        { flexDirection: 'column' } ]}>
                                        <ClickableTitle numberOfLines={4}>{description}</ClickableTitle>
                                    </View>
                                </View>
                            );
                        };

                        return (
                            <View
                                style={{
                                    flex: 1,
                                    marginHorizontal: 0,
                                    paddingHorizontal: 0,
                                }}
                            >
                                {sortBy(out, 'order')
                                    .map((i, idx) => {
                                        return (
                                            <React.Fragment key={`out_${idx}`}>
                                                <View
                                                    style={[
                                                        {
                                                            flexDirection: 'row',
                                                            paddingBottom: paddingSize,
                                                            paddingTop: paddingSize,
                                                            // paddingBottom: dt?.description ? 4 : 16,
                                                            borderBottomWidth: 1,
                                                            borderBottomColor: COLORS.BORDER_GRAY,
                                                            paddingHorizontal: 6,
                                                        },
                                                    ]}
                                                >
                                                    <Title style={[ styles.title ]}>{`${title}:`}</Title>
                                                    <View style={[ styles.rightTitleBlock,
                                                        { flexDirection: 'column' } ]}>
                                                        {i?.data?.map?.((dt, idx1) => {
                                                            return (
                                                                <React.Fragment key={`data_link_${idx1}`}>
                                                                    <Link
                                                                        href={() =>
                                                                            navigation.navigate('MedCenterDetails', {
                                                                                mdInfo: {
                                                                                    ...dt,
                                                                                    _id: dt?.branchId,
                                                                                },
                                                                            })
                                                                        }
                                                                    >
                                                                        {dt?.name}
                                                                    </Link>
                                                                    <Subtitle>{dt?.address}</Subtitle>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </View>
                                                </View>
                                                {i.data?.map((dt, idx1) => {
                                                    return (
                                                        <Description
                                                            description={dt?.description}
                                                            key={`data_${idx1}`}
                                                        />
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                            </View>
                        );
                    }}
                />
            </Section>
        </Page>
    );
});
